import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Clock,
  Loader2,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@foundation/components/ui/card";
import { Badge } from "@foundation/components/ui/badge";
import { Button } from "@foundation/components/ui/button";
import { useToast } from "@foundation/hooks/use-toast";
import { useActiveWorkspaceRole } from "../../../header/useActiveWorkspaceRole";
import {
  listPendingApprovals,
  type PolicyApprovalSummary,
} from "../services/boostPolicyService";

// Format an ISO timestamp for display; falls back to the raw string on parse failure.
const formatWhen = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Policy Approvals — org-admin-only queue of member-submitted policies.
 *
 * The queue is a simple list. Clicking a request opens the SAME boost-policy
 * preview page members use (/revisr/brand-policy/preview), but with ?review=approval
 * so the editor shows Accept / Reject (+ rejection reason) instead of Save/Export.
 *
 * Refresh: the list refetches on mount (navigation), on window focus (so returning
 * to the tab picks up decisions made elsewhere), and via a manual Refresh button
 * that shows a spinning state while fetching. No hard page reloads.
 */
export const PolicyApprovals = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isOrgAdmin, isLoading: roleLoading } = useActiveWorkspaceRole();

  const [approvals, setApprovals] = useState<PolicyApprovalSummary[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadList = useCallback(async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true);
    else setIsLoadingList(true);
    try {
      const list = await listPendingApprovals();
      setApprovals(list);
    } catch (err) {
      console.error("[PolicyApprovals] Failed to load approvals:", err);
      toast({
        title: "Couldn't load approvals",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      if (isRefresh) setIsRefreshing(false);
      else setIsLoadingList(false);
    }
  }, [toast]);

  useEffect(() => {
    loadList(false);
  }, [loadList]);

  // Auto-refresh when the admin returns focus to the tab (catches decisions made
  // on the preview page in another tab, or status changes). No page reload.
  useEffect(() => {
    const onFocus = () => loadList(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadList]);

  const requestedBy = (a: PolicyApprovalSummary): string =>
    a.requested_by_name || a.requested_by || "—";

  // Only org admins can review approval requests. Members are redirected away,
  // so the page is never reachable (or renderable) by non-admins even via URL.
  // Placed AFTER all hooks so the hook order stays stable across renders.
  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
      </div>
    );
  }
  if (!isOrgAdmin) {
    return <Navigate to="/revisr/dashboard" replace />;
  }

  const handleReview = (policyId: string) => {
    // Open the boost-policy preview page in admin-review mode. The Accept/Reject
    // controls render there only when arriving via the approvals queue.
    navigate(`/revisr/brand-policy/preview?tab=preview&id=${policyId}&review=approval`);
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-5xl">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            Policy Approvals
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review policies submitted by members. Open a request to preview the policy and accept or reject it.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadList(true)}
          disabled={isRefreshing}
          className="gap-1.5 shrink-0"
          title={isRefreshing ? "Refreshing..." : "Refresh approvals"}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending requests
            {approvals.length > 0 && (
              <Badge variant="secondary" className="ml-1">{approvals.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>Open a policy to review it on the preview page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoadingList ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
            </div>
          ) : approvals.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No policies awaiting approval.</p>
            </div>
          ) : (
            approvals.map((a) => (
              <button
                key={a.id}
                onClick={() => handleReview(a.id)}
                className="w-full text-left rounded-lg border border-border hover:bg-muted/50 p-4 transition-colors flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{a.policy_name || "Untitled policy"}</span>
                    <Badge variant="outline" className="shrink-0">Pending</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Requested {formatWhen(a.requested_at)} · By {requestedBy(a)}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary shrink-0">
                  Review <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </button>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
